import {
  GENRE_NAMES,
  LISTING_LIMITS,
  LISTING_TAGS,
  type Listing,
  type ListingBlock,
  type ListingImage,
  type ListingInline,
  RATING_NAMES,
  ratingOf,
} from '@mogzo/shared';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

/** A run of How to play text, with its bold, italic and key marks. */
function Run({ run }: { run: ListingInline }) {
  let node: ReactNode = run.text;
  if (run.key) node = <kbd>{node}</kbd>;
  if (run.italic) node = <em>{node}</em>;
  if (run.bold) node = <strong>{node}</strong>;
  return node;
}

function Runs({ runs }: { runs: ListingInline[] }) {
  // Runs are never reordered, so their place is their key.
  // biome-ignore lint/suspicious/noArrayIndexKey: see above
  return runs.map((run, i) => <Run key={i} run={run} />);
}

/** How to play as players read it, from MOGZO's blocks (never the developer's HTML). */
export function HowToPlayView({
  blocks,
  images,
}: {
  blocks: ListingBlock[];
  images: Map<string, ListingImage>;
}) {
  return (
    <div className="listing-text">
      {blocks.map((block, i) => {
        // Blocks have no ids; their place in the text is their key.
        const key = i;
        switch (block.type) {
          case 'heading':
            return block.level === 2 ? (
              <h3 key={key}>
                <Runs runs={block.text} />
              </h3>
            ) : (
              <h4 key={key} className="text-base font-bold">
                <Runs runs={block.text} />
              </h4>
            );
          case 'paragraph':
            return (
              <p key={key}>
                <Runs runs={block.text} />
              </p>
            );
          case 'tip':
            return (
              <aside key={key}>
                <Runs runs={block.text} />
              </aside>
            );
          case 'list': {
            const List = block.ordered ? 'ol' : 'ul';
            return (
              <List key={key}>
                {block.items.map((item, n) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: as above
                  <li key={n}>
                    <Runs runs={item} />
                  </li>
                ))}
              </List>
            );
          }
          case 'picture': {
            const url = images.get(block.imageId)?.url;
            return url ? (
              <figure key={key} className="max-w-[420px]">
                <img src={url} alt={block.caption} />
                {block.caption && <figcaption>{block.caption}</figcaption>}
              </figure>
            ) : null;
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

const PLAY = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5Z" />
  </svg>
);

const STRIPES = {
  background: 'repeating-linear-gradient(135deg, #efecf5 0 14px, #e8e4f0 14px 28px)',
};

function Placeholder({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className: string;
  style?: CSSProperties;
}) {
  return (
    <span
      style={{ ...STRIPES, ...style }}
      className={`grid place-items-center text-center ${className}`}
    >
      <span className="rounded-xl bg-white/90 px-3.5 py-2 text-sm font-bold">{children}</span>
    </span>
  );
}

/**
 * On a computer the game page shows the cover's middle band, 16 : 5 of its 16 : 9: this much of
 * its height is cut from the top, and as much from the bottom.
 */
export const COVER_CROP = (1 - 5 / 9) / 2;

type Shot = ListingImage & { url: string };

const ratioOf = (shot: Shot) => (shot.width && shot.height ? shot.width / shot.height : 16 / 9);

/**
 * A picture's width in the screenshot row, for its shape. Wide sets show three at once on a
 * computer and one on a phone, with the next peeking in; a set of mostly tall screenshots gets a
 * taller row, so each keeps its shape and more fit.
 */
function rowWidth(ratio: number, tall: boolean, phone: boolean): string {
  const [share, gaps, base] = phone
    ? tall
      ? [0.44, 0, 9 / 16]
      : [0.86, 0, 16 / 9]
    : tall
      ? [1 / 5, 64, 9 / 16]
      : [1 / 3, 32, 16 / 9];
  return `calc((100% - ${gaps}px) * ${(share * ratio) / base})`;
}

const ARROW = 'grid size-11 place-items-center rounded-full text-2xl leading-none font-bold';

/** A screenshot at full size, over the page; arrows and Esc work as on the game page. */
function Viewer({
  shots,
  index,
  phone,
  onIndex,
  onClose,
}: {
  shots: Shot[];
  index: number;
  phone: boolean;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  const shot = shots[index];
  const go = (step: number) => onIndex((index + step + shots.length) % shots.length);
  if (!shot) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Screenshot ${index + 1} of ${shots.length}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // Only the viewer closes, not what it's drawn in.
          event.stopPropagation();
          onClose();
        } else if (event.key === 'ArrowLeft') go(-1);
        else if (event.key === 'ArrowRight') go(1);
      }}
      className="fixed inset-0 z-[60] flex flex-col bg-[#0b0a1d]/95 text-white"
    >
      <div className="flex items-center justify-between p-3">
        <span className="px-2 text-sm font-bold tabular-nums">
          {index + 1} / {shots.length}
        </span>
        <button
          ref={close}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`${ARROW} hover:bg-white/10`}
        >
          ×
        </button>
      </div>
      <div
        className={`relative flex min-h-0 flex-1 items-center justify-center pb-6 ${phone ? 'px-3' : 'px-20'}`}
      >
        <img
          src={shot.url}
          alt={`Screenshot ${index + 1}`}
          className="max-h-full max-w-full rounded-xl object-contain"
        />
        {shots.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous screenshot"
              onClick={() => go(-1)}
              className={`${ARROW} absolute top-1/2 left-3 -translate-y-1/2 bg-white/90 text-ink`}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next screenshot"
              onClick={() => go(1)}
              className={`${ARROW} absolute top-1/2 right-3 -translate-y-1/2 bg-white/90 text-ink`}
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The trailer and screenshots in one row that scrolls sideways: buttons at its ends on a
 * computer, a swipe and dots on a phone. A screenshot opens full size.
 */
function Gallery({
  shots,
  trailerUrl,
  phone,
}: {
  shots: Shot[];
  trailerUrl: string;
  phone: boolean;
}) {
  const row = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ start: true, end: true });
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const tall = shots.filter((shot) => ratioOf(shot) < 1).length > shots.length / 2;
  const empty = Math.max(0, LISTING_LIMITS.screenshotsMin - shots.length);
  const count = (trailerUrl ? 1 : 0) + shots.length + empty;
  const base = tall ? 9 / 16 : 16 / 9;

  useEffect(() => {
    const element = row.current;
    if (!element || !count) return;
    const update = () => {
      const left = element.scrollLeft;
      setEnds({
        start: left < 4,
        end: left + element.clientWidth >= element.scrollWidth - 4,
      });
      // The picture nearest the row's start is the one a phone shows.
      const padding = Number.parseFloat(getComputedStyle(element).paddingLeft) || 0;
      const away = (item: HTMLElement) => Math.abs(item.offsetLeft - padding - left);
      const items = Array.from(element.children) as HTMLElement[];
      let nearest = 0;
      items.forEach((item, i) => {
        const best = items[nearest];
        if (best && away(item) < away(best)) nearest = i;
      });
      setCurrent(nearest);
    };
    update();
    element.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      element.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [count]);

  const scroll = (direction: 1 | -1) => {
    const element = row.current;
    element?.scrollBy({ left: direction * element.clientWidth * 0.9, behavior: 'smooth' });
  };
  const closeViewer = () => {
    const was = open;
    setOpen(null);
    row.current?.querySelector<HTMLElement>(`[data-shot="${was}"]`)?.focus();
  };
  const tile = 'relative flex-none snap-start overflow-hidden rounded-2xl';

  return (
    <section aria-label="Trailer and screenshots" className="relative">
      <div
        ref={row}
        className={`flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] ${
          phone ? 'scroll-px-4 gap-2.5 px-4' : 'gap-4'
        }`}
      >
        {trailerUrl && (
          <a
            href={trailerUrl}
            target="_blank"
            rel="noreferrer"
            // As wide as a wide row's picture and as tall as this row; the video letterboxes.
            style={{ width: rowWidth(16 / 9, false, phone) }}
            className={`${tile} flex flex-col items-center justify-center gap-1.5 bg-ink p-3 text-center text-white`}
          >
            <span className="mb-1 grid size-14 place-items-center rounded-full bg-white/15">
              {PLAY}
            </span>
            <strong>Trailer</strong>
            <span className="text-[13px] text-[#c9c4dd]">It plays here on the game page.</span>
          </a>
        )}
        {shots.map((shot, index) => (
          <button
            key={shot.id}
            type="button"
            data-shot={index}
            aria-label={`Screenshot ${index + 1}, open it full size`}
            onClick={() => setOpen(index)}
            style={{ width: rowWidth(ratioOf(shot), tall, phone), aspectRatio: `${ratioOf(shot)}` }}
            className={`${tile} cursor-zoom-in bg-ink`}
          >
            <img src={shot.url} alt="" className="size-full object-cover" />
          </button>
        ))}
        {Array.from({ length: empty }, (_, i) => (
          <Placeholder
            // biome-ignore lint/suspicious/noArrayIndexKey: empty slots
            key={i}
            className={tile}
            style={{ width: rowWidth(base, tall, phone), aspectRatio: `${base}` }}
          >
            Screenshot {shots.length + i + 1} goes here
          </Placeholder>
        ))}
      </div>
      {!phone && !ends.start && (
        <button
          type="button"
          aria-label="Scroll back"
          onClick={() => scroll(-1)}
          className={`${ARROW} absolute top-1/2 left-3 -translate-y-1/2 bg-white shadow-lg`}
        >
          ‹
        </button>
      )}
      {!phone && !ends.end && (
        <button
          type="button"
          aria-label="See more"
          onClick={() => scroll(1)}
          className={`${ARROW} absolute top-1/2 right-3 -translate-y-1/2 bg-white shadow-lg`}
        >
          ›
        </button>
      )}
      {phone && count > 1 && (
        <div aria-hidden="true" className="flex justify-center gap-1.5 pt-2.5">
          {Array.from({ length: count }, (_, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: one dot per picture
              key={i}
              className={`size-1.5 rounded-full ${i === current ? 'bg-ink' : 'bg-[#cfcadb]'}`}
            />
          ))}
        </div>
      )}
      {open !== null && (
        <Viewer shots={shots} index={open} phone={phone} onIndex={setOpen} onClose={closeViewer} />
      )}
    </section>
  );
}

const PLAYS_ON = {
  computer: 'Computers',
  phone: 'Phones',
} as const;

function playsOn(listing: Listing): string {
  const devices = listing.devices.map((d, i) => (i ? PLAYS_ON[d].toLowerCase() : PLAYS_ON[d]));
  const held =
    listing.orientation === 'landscape'
      ? ', in landscape'
      : listing.orientation === 'portrait'
        ? ', in portrait'
        : '';
  return devices.length ? `${devices.join(' and ')}${held}` : 'Not set';
}

/**
 * A store listing as its game page on mogzo.com (M20), on a computer or a phone. As a preview,
 * it draws mogzo.com's top bar, and Play, Follow and the tabs do nothing; `viewer` is who's
 * looking, for the avatar. `live` is the real page (M23): the site draws the top bar, Play
 * works, and what isn't built yet (Follow, Share, the tabs) is left out.
 */
export function GamePage({
  listing,
  images,
  viewer,
  phone,
  live,
  aboutOnly = false,
}: {
  listing: Listing;
  images: Map<string, ListingImage>;
  viewer: string;
  phone: boolean;
  live?: { onPlay: () => void; notice?: ReactNode };
  /**
   * Only the store listing under the header: screenshots, About, How to play and Details, for
   * the game page's About tab on mogzo.com (M29), which draws its own header.
   */
  aboutOnly?: boolean;
}) {
  // Inside a page that has its own <main>, the listing is a plain block.
  const Body = aboutOnly ? 'div' : 'main';
  const url = (id: string | null) => (id ? images.get(id)?.url : undefined);
  const cover = url(listing.coverId);
  const icon = url(listing.iconId);
  const shots = listing.screenshotIds
    .map((id) => images.get(id))
    .filter((shot): shot is Shot => Boolean(shot?.url));
  const developer = listing.developer;
  const rating = RATING_NAMES[ratingOf(listing.rating)];
  const paragraphs = listing.about
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const card = 'rounded-[20px] border border-line bg-card';
  const button =
    'inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-[15px] font-bold';

  const details = (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
      <dt className="font-bold text-muted">Genre</dt>
      <dd className="font-semibold">{listing.genre ? GENRE_NAMES[listing.genre] : 'Not set'}</dd>
      <dt className="font-bold text-muted">Tags</dt>
      <dd className="flex flex-wrap gap-1.5">
        {listing.tags.length
          ? listing.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-line bg-field px-2.5 py-0.5 text-xs font-bold"
              >
                {LISTING_TAGS[tag]}
              </span>
            ))
          : 'None yet'}
      </dd>
      <dt className="font-bold text-muted">Plays on</dt>
      <dd className="font-semibold">{playsOn(listing)}</dd>
      <dt className="font-bold text-muted">Rating</dt>
      <dd className="font-semibold">{rating}</dd>
      <dt className="font-bold text-muted">Developer</dt>
      <dd className="font-semibold">{developer || 'Not set'}</dd>
    </dl>
  );

  const iconClass = `flex-none border-4 border-white shadow-lg ${
    phone ? '-mt-9 size-[84px] rounded-[20px]' : '-mt-12 size-28 rounded-[26px]'
  }`;
  const iconEl = icon ? (
    <img src={icon} alt="" className={iconClass} />
  ) : (
    <Placeholder className={iconClass}>Icon</Placeholder>
  );
  const titleEl = (
    <h1
      id="game-title"
      className={`font-display leading-tight font-bold ${phone ? 'text-[28px]' : 'text-[40px]'}`}
    >
      {listing.title || 'Untitled game'}
    </h1>
  );
  const ratingChip = (
    <span className="rounded-full bg-ink px-2.5 py-0.5 text-xs font-bold text-white">{rating}</span>
  );

  const play = (className: string) =>
    live ? (
      <button type="button" onClick={live.onPlay} className={`${className} hover:bg-brand-hover`}>
        {PLAY}
        Play
      </button>
    ) : (
      <span className={className}>
        {PLAY}
        Play
      </span>
    );

  return (
    <div className="flex-1 bg-canvas text-ink">
      {!live && !aboutOnly && (
        <header className="border-b border-divider bg-card">
          <div className="mx-auto flex h-16 max-w-[1180px] items-center justify-between px-4 sm:px-6">
            <span className="flex items-center gap-2.5">
              <img src="/brand/mascot.svg" alt="" className="h-[30px] w-auto" />
              <span className="font-display text-[26px] leading-none font-bold">mogzo</span>
            </span>
            <span
              aria-hidden="true"
              className="grid size-9 place-items-center rounded-full bg-brand font-bold"
            >
              {viewer.slice(0, 1).toUpperCase()}
            </span>
          </div>
        </header>
      )}

      <Body
        className={`mx-auto flex max-w-[1180px] flex-col ${phone ? 'gap-4 pb-6' : aboutOnly ? 'gap-6' : 'gap-6 p-6'}`}
      >
        {!aboutOnly && (
          <>
            {live?.notice && <div className={phone ? 'px-4 pt-4' : ''}>{live.notice}</div>}
            <section
              aria-labelledby="game-title"
              className={phone ? 'border-b border-line bg-card' : `${card} overflow-hidden`}
            >
              {cover ? (
                <img
                  src={cover}
                  alt=""
                  className={`block w-full object-cover ${phone ? 'aspect-video' : 'aspect-[16/5]'}`}
                />
              ) : (
                <Placeholder className={phone ? 'aspect-video' : 'aspect-[16/5]'}>
                  Cover goes here
                </Placeholder>
              )}
              {phone ? (
                <div className="flex flex-col gap-3 px-4 pb-4">
                  <div className="flex items-end gap-3.5">
                    {iconEl}
                    <div className="min-w-0">
                      {titleEl}
                      <p className="mt-0.5 text-sm text-muted">
                        {developer && (
                          <>
                            by <strong className="text-ink">{developer}</strong>
                            {listing.genre ? ' · ' : ''}
                          </>
                        )}
                        {listing.genre ? GENRE_NAMES[listing.genre] : ''}
                      </p>
                    </div>
                  </div>
                  {listing.shortDescription && (
                    <p className="text-plum">{listing.shortDescription}</p>
                  )}
                  <span className="flex flex-wrap gap-1.5">
                    {ratingChip}
                    {listing.devices.includes('phone') && listing.orientation !== 'any' && (
                      <span className="rounded-full border border-line bg-field px-2.5 py-0.5 text-xs font-bold">
                        {listing.orientation === 'landscape'
                          ? 'Turn your phone sideways'
                          : 'Hold your phone upright'}
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2.5">
                    {play(`${button} flex-1 bg-brand`)}
                    {!live && (
                      <span className={`${button} border border-line bg-card`}>Follow</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-x-5 gap-y-3 px-7 pb-6">
                  {iconEl}
                  <div className="min-w-0 flex-[1_1_300px] pt-3">
                    {titleEl}
                    {listing.shortDescription && (
                      <p className="mt-1.5 text-[17px] text-plum">{listing.shortDescription}</p>
                    )}
                    <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
                      {developer && (
                        <>
                          <span>
                            by <strong className="text-ink">{developer}</strong>
                          </span>
                          <span aria-hidden="true">·</span>
                        </>
                      )}
                      {listing.genre && (
                        <>
                          <span>{GENRE_NAMES[listing.genre]}</span>
                          <span aria-hidden="true">·</span>
                        </>
                      )}
                      {ratingChip}
                      <span aria-hidden="true">·</span>
                      <span>New on MOGZO</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {play(`${button} min-w-36 bg-brand`)}
                    {!live && (
                      <>
                        <span className={`${button} border border-line bg-card`}>Follow</span>
                        <span className={`${button} border border-line bg-card`}>Share</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              {!live && (
                <div
                  aria-hidden="true"
                  className={`flex gap-7 overflow-x-auto [scrollbar-width:none] border-t border-[#ece9f2] font-bold text-muted ${phone ? 'px-4' : 'px-7'}`}
                >
                  <span className="border-b-[3px] border-brand py-3 text-ink">About</span>
                  <span className="py-3">Posts</span>
                  <span className="py-3">Leaderboards</span>
                  <span className="py-3">Achievements</span>
                </div>
              )}
            </section>
          </>
        )}

        <Gallery shots={shots} trailerUrl={listing.trailerUrl} phone={phone} />

        <div className={`flex flex-wrap items-start gap-6 ${phone ? 'px-4' : ''}`}>
          <div className="flex min-w-0 flex-[999_1_480px] flex-col gap-6">
            <section
              aria-labelledby="about-heading"
              className={`${card} ${phone ? 'p-[18px]' : 'px-7 py-6'}`}
            >
              <h2 id="about-heading" className="mb-2.5 text-[22px] font-bold">
                About
              </h2>
              {paragraphs.length ? (
                <div className="flex flex-col gap-3 leading-relaxed whitespace-pre-line">
                  {paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              ) : (
                <p className="text-muted">Nothing here yet.</p>
              )}
            </section>
            <section
              aria-labelledby="howto-heading"
              className={`${card} ${phone ? 'p-[18px]' : 'px-7 py-6'}`}
            >
              <h2 id="howto-heading" className="mb-2.5 text-[22px] font-bold">
                How to play
              </h2>
              {listing.howToPlay.length ? (
                <HowToPlayView blocks={listing.howToPlay} images={images} />
              ) : (
                <p className="text-muted">Nothing here yet.</p>
              )}
            </section>
          </div>
          <aside
            aria-labelledby="details-heading"
            className={`${card} min-w-0 flex-[1_1_280px] p-6 ${phone ? '' : 'max-w-[360px]'}`}
          >
            <h2 id="details-heading" className="mb-3.5 text-lg font-bold">
              Details
            </h2>
            {details}
          </aside>
        </div>
      </Body>
    </div>
  );
}
